// src/components/CustomerQuoteBuilder.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { FiTrash2 } from "react-icons/fi";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import Select from "react-select";
import { fetchQuoteMasterItems } from "../services/quoteMaster.service";
import "./CustomerQuoteBuilder.css";

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.quote.softvolute.in";

// A document may be attached only when its FINAL file size is <= 5 MiB.
// Large images are compressed in the browser. PDFs/Office files need the
// optional server endpoint; never silently alter or rename a document.
const MAX_DOCUMENT_SIZE = 5 * 1024 * 1024;
const MAX_COMPRESSION_INPUT = 30 * 1024 * 1024;
const MAX_DOCUMENT_COUNT = 5;
const DOCUMENT_COMPRESS_URL =
  process.env.REACT_APP_DOCUMENT_COMPRESS_URL ||
  `${BASE_URL}/api/quotes/compress-document`;

const formatDocumentSize = (bytes) =>
  `${(Number(bytes || 0) / (1024 * 1024)).toFixed(2)} MB`;

const documentExtension = (name) =>
  String(name || "")
    .split(".")
    .pop()
    .toLowerCase();

// Browser preview supports PDFs and images. Word/Excel require a native app.
const DOCUMENT_MIME_TYPES = {
  pdf: "application/pdf",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
};

function getDocumentLocation(doc) {
  const source = doc?.data || doc?.url || "";
  if (typeof source !== "string" || !source.trim()) {
    throw new Error(
      "Document data missing. Re-upload the file and save the quote.",
    );
  }
  // Previously stored blob URLs are valid only in their original browser session.
  if (source.startsWith("blob:")) {
    throw new Error(
      "This temporary document link has expired. Re-upload and save it.",
    );
  }
  if (source.startsWith("data:")) return { data: source };
  if (/^https?:\/\//i.test(source) || source.startsWith("/")) {
    return {
      url: source.startsWith("/")
        ? `${BASE_URL.replace(/\/$/, "")}${source}`
        : source,
    };
  }
  throw new Error("Unsupported document location. Re-upload the document.");
}

function documentBlobFromData(doc, dataUrl) {
  // Uploaded documents are stored using FileReader.readAsDataURL.
  const match = /^data:([^;,]*)(;base64)?,([\s\S]*)$/i.exec(dataUrl);
  if (!match || !match[2]) {
    throw new Error("Document data is incomplete. Re-upload the document.");
  }
  const binary = atob(match[3].replace(/\s/g, ""));
  if (!binary.length) throw new Error("Document is empty. Re-upload it.");
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const ext = documentExtension(doc?.name);
  const mime =
    DOCUMENT_MIME_TYPES[ext] || match[1] || "application/octet-stream";
  // A corrupt or wrongly encoded PDF otherwise displays a blank PDF viewer.
  if (ext === "pdf" && binary.slice(0, 5) !== "%PDF-") {
    throw new Error(
      "Saved PDF is invalid. Please upload the original PDF again.",
    );
  }
  return new Blob([bytes], { type: mime });
}

// JPEG keeps its format, and PNG stays PNG (including transparency).
async function shrinkImageUnderLimit(file) {
  const bitmap = await createImageBitmap(file);
  try {
    const sourceWidth = bitmap.width;
    const sourceHeight = bitmap.height;
    const maxEdge = Math.max(sourceWidth, sourceHeight);
    if (!maxEdge) throw new Error("Cannot read image dimensions.");

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx)
      throw new Error("Image compression is unavailable in this browser.");

    const extension = documentExtension(file.name);
    const outputType = extension === "png" ? "image/png" : "image/jpeg";
    let best = null;

    for (let attempt = 0; attempt < 16; attempt += 1) {
      const dimensionScale =
        Math.min(1, 2800 / maxEdge) * Math.pow(0.83, Math.floor(attempt / 3));
      canvas.width = Math.max(1, Math.round(sourceWidth * dimensionScale));
      canvas.height = Math.max(1, Math.round(sourceHeight * dimensionScale));
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (outputType === "image/jpeg") {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

      const quality = Math.max(0.46, 0.88 - (attempt % 3) * 0.15);
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (value) =>
            value
              ? resolve(value)
              : reject(new Error("Image compression failed.")),
          outputType,
          quality,
        );
      });

      if (!best || blob.size < best.size) best = blob;
      if (blob.size <= MAX_DOCUMENT_SIZE) {
        return new File([blob], file.name, {
          type: outputType,
          lastModified: file.lastModified,
        });
      }
    }

    throw new Error(
      `Image could not be reduced below 5 MB (best ${formatDocumentSize(best?.size)}).`,
    );
  } finally {
    bitmap.close?.();
  }
}

// Server must return the compressed ORIGINAL file as binary, preserving format.
// An Express implementation is supplied separately; its route must be mounted.
async function compressLargeDocumentOnServer(file) {
  try {
    const form = new FormData();
    form.append("file", file, file.name);
    const response = await axios.post(DOCUMENT_COMPRESS_URL, form, {
      responseType: "blob",
      timeout: 120000,
    });
    const blob = response.data;
    if (!(blob instanceof Blob) || blob.size <= 0) {
      throw new Error("Compression server returned an empty file.");
    }
    if (blob.type.includes("json") || blob.type.includes("html")) {
      throw new Error("Compression server did not return a document.");
    }
    if (blob.size > MAX_DOCUMENT_SIZE) {
      throw new Error(
        `Document is still ${formatDocumentSize(blob.size)} after compression.`,
      );
    }
    return new File([blob], file.name, {
      type: file.type || blob.type || "application/octet-stream",
      lastModified: file.lastModified,
    });
  } catch (error) {
    let message = error.message || "Server compression failed.";
    if (error.response?.status === 404) {
      message =
        "Document compression API not installed. Please mount /api/quotes/compress-document on your backend.";
    } else if (
      error.response?.data instanceof Blob &&
      error.response.data.type.includes("json")
    ) {
      try {
        const details = JSON.parse(await error.response.data.text());
        message = details.message || details.error || message;
      } catch (_) {
        /* leave original error */
      }
    }
    throw new Error(message);
  }
}

async function optimizeDocument(file) {
  if (file.size <= MAX_DOCUMENT_SIZE) return file;
  if (file.size > MAX_COMPRESSION_INPUT) {
    throw new Error(
      `${file.name}: Source exceeds the 30 MB compression input safety limit.`,
    );
  }
  const extension = documentExtension(file.name);
  const reduced = ["jpg", "jpeg", "png"].includes(extension)
    ? await shrinkImageUnderLimit(file)
    : await compressLargeDocumentOnServer(file);
  if (reduced.size > MAX_DOCUMENT_SIZE) {
    throw new Error(`${file.name} cannot be reduced below 5 MB.`);
  }
  return reduced;
}

/* a single line item */
const rowTemplate = () => ({
  image: "",
  name: "",
  room: "",
  unit: "NOS",
  quantity: 1,
  rate: 0,
  length: 0,
  width: 0,
  mrp: 0,
  isManual: true, // ✅ Only + Add Line rows will get image upload option
});

const roomOptions = [
  "Living Room",
  "Kitchen",
  "Dining",
  "Pooja Room",
  "Master Bedroom",
  "Bedroom-1",
  "Bedroom-2",
  "Bedroom-3",
  "Bedroom-4",
  "Bedroom-5",
  "Kids Bedroom",
  "Parents Bedroom",
  "Guest Bedroom",
  "Storage",
  "Server Room",
  "Wash Room",
  "Pantry",
  "Utility",
  "Overall Flat",
  "Foyer Area",
  "Vestibule",
];

export default function CustomerQuoteBuilder() {
  const navigate = useNavigate();
  const { state } = useLocation();

  // edit mode?
  const initialQuote = state?.quote || null;
  const initialQuoteId = state?.quoteId || initialQuote?._id || null;
  const isEdit = Boolean(initialQuote || initialQuoteId);

  // top form fields
  const [projectName, setProjectName] = useState(state?.projectName || "");
  const [clientName, setClientName] = useState(state?.clientName || "");
  const [clientAddress, setClientAddress] = useState(
    state?.clientAddress || "",
  );
  const [title, setTitle] = useState("Custom Quote");
  const [rows, setRows] = useState([rowTemplate()]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState("NOS");
  const [selectedQty, setSelectedQty] = useState(1);
  const [quoteMasterOptions, setQuoteMasterOptions] = useState([]);
  const [masterItemsOptions, setMasterItemsOptions] = useState([]);
  // Independent optional charges — same behavior as Quotes.jsx
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [applyDiscount, setApplyDiscount] = useState(false);
  const [pmFeePercentage, setPmFeePercentage] = useState(0);
  const [applyPmFee, setApplyPmFee] = useState(false);
  const [gstPercentage, setGstPercentage] = useState(18);
  const [applyGst, setApplyGst] = useState(false);
  const [gstMode, setGstMode] = useState("exclusive");
  // Documents uploaded separately to backend disk; quote stores only file metadata.
  const [documents, setDocuments] = useState([]);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const previewObjectUrlRef = useRef(null);

  const closeDocumentPreview = () => {
    setPreviewDocument(null);
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current);
        previewObjectUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!previewDocument) return undefined;
    const onEscape = (event) => {
      if (event.key === "Escape") closeDocumentPreview();
    };
    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  }, [previewDocument]);

  // Client detail fetch
  useEffect(() => {
    if (!projectName || isEdit) return;

    const fetchClientDetails = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/client-details/${encodeURIComponent(projectName)}`,
        );

        const details = res.data?.clientDetails || {};

        setClientName(
          details.ClientName ||
            projectName.replace(/^\d{2}-\d{4}-\d{2}[_\s-]+/, "").trim(),
        );

        setClientAddress(details.siteAddress || details.clientAddress || "");
      } catch (err) {
        console.warn("Client detail not found");
      }
    };

    fetchClientDetails();
  }, [projectName, isEdit]);
  // backend status
  const [status, setStatus] = useState("Rejected");

  // Prefill in edit mode
  useEffect(() => {
    const hydrateFromQuote = (q) => {
      try {
        setTitle(q?.title || "Custom Quote");
        setProjectName(q?.projectName || "");
        setClientName(q?.clientName || "");
        setClientAddress(q?.clientAddress || "");
        setStatus(q?.status || "Rejected");
        const savedDiscountPct = Number(
          q?.discountPercentage ?? q?.globalDiscountPct ?? 0,
        );
        const articleValue = Number(q?.totalMRP || 0);
        const savedPmPct = Number(
          q?.projectManagementFeePercentage ??
            (articleValue > 0
              ? (Number(q?.projectManagementFee || 0) / articleValue) * 100
              : 0),
        );
        const savedGstMode =
          q?.gstMode === "inclusive" || q?.inclusiveGST === true
            ? "inclusive"
            : "exclusive";
        const savedGstAmount = Number(q?.gstAmount || 0);
        const gstBase =
          savedGstMode === "inclusive"
            ? Number(q?.finalAmount || 0) - savedGstAmount
            : articleValue +
              Number(q?.projectManagementFee || 0) -
              Number(q?.discountAmount || 0);
        const savedGstPct = Number(
          q?.gstPercentage ??
            (gstBase > 0 ? (savedGstAmount / gstBase) * 100 : 0),
        );

        setDiscountPercentage(savedDiscountPct);
        setApplyDiscount(savedDiscountPct > 0 || Boolean(q?.applyDiscount));
        setPmFeePercentage(Number(savedPmPct.toFixed(2)));
        setApplyPmFee(savedPmPct > 0 || Boolean(q?.applyProjectManagementFee));
        setGstPercentage(
          !q?.applyGST && savedGstPct === 0
            ? 18
            : Number(savedGstPct.toFixed(2)),
        );
        setApplyGst(savedGstPct > 0 || Boolean(q?.applyGST));
        setGstMode(savedGstMode);
        setDocuments(Array.isArray(q?.documents) ? q.documents : []);

        const mapped =
          Array.isArray(q?.items) && q.items.length
            ? q.items.map((it) => ({
                image: it.image || "",
                name: it.label || it.value || "Custom Item",
                room: it.room || "",
                unit: ["QNT", "NOS", "SQ.FT"].includes(it.unit)
                  ? it.unit
                  : "NOS",
                quantity: Number(it.quantity) > 0 ? Number(it.quantity) : 1,
                // Legacy quotes contain total MRP only. Do not multiply their old amount again.
                rate: Number(
                  it.rate ??
                    Number(it.MRP ?? it.price ?? 0) /
                      (Number(it.quantity) > 0 ? Number(it.quantity) : 1),
                ),
                length: Number(it.length || 0),
                width: Number(it.width || 0),
                mrp: Number(it.MRP ?? it.price ?? 0),
                isManual: typeof it.isManual === "boolean" ? it.isManual : true,
              }))
            : [rowTemplate()];

        setRows(mapped);
      } catch (e) {
        console.error("Failed mapping quote to rows:", e);
      }
    };

    if (initialQuote) {
      hydrateFromQuote(initialQuote);
    } else if (initialQuoteId) {
      (async () => {
        try {
          const { data } = await axios.get(
            `${BASE_URL}/api/quotes/${initialQuoteId}`,
          );
          hydrateFromQuote(data?.quote || data);
        } catch (e) {
          console.error("Failed to fetch quote:", e);
          alert("Failed to load existing quote.");
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuoteId]);

  // Load Quote Master + old Master Items so Customer Quote can use both sources
  useEffect(() => {
    const loadQuoteItems = async () => {
      try {
        const quoteMasterData = await fetchQuoteMasterItems();

        const formattedQuoteMaster = (
          Array.isArray(quoteMasterData) ? quoteMasterData : []
        ).map((item) => ({
          itemId: item._id || item.itemId,
          label: item.label || item.value || "Unnamed Item",
          value: item.label || item.value || item._id,
          description: item.description || "",
          image: item.image || "",
          MRP: Number(item.MRP || item.price || 0),
          length: Number(item.length || 0),
          width: Number(item.width || 0),
          category: item.category || "",
          source: "Quote Master",
        }));

        const masterRes = await axios.get(`${BASE_URL}/api/master-items`);

        const formattedMasterItems = (
          Array.isArray(masterRes.data) ? masterRes.data : []
        ).map((item) => ({
          itemId: item.itemId || item._id,
          label: item.label || item.value || "Unnamed Item",
          value: item.value || item.label || item._id,
          description: item.description || "",
          image: item.image || "",
          MRP: Number(item.MRP || item.price || 0),
          length: Number(item.length || 0),
          width: Number(item.width || 0),
          category: item.category || "Custom",
          source: "Master Item",
        }));

        setQuoteMasterOptions(formattedQuoteMaster);
        setMasterItemsOptions(formattedMasterItems);
      } catch (err) {
        console.error("Customer quote item load failed:", err);
      }
    };

    loadQuoteItems();
  }, []);

  // row helpers
  const addRow = () => setRows((r) => [...r, rowTemplate()]);
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const setRow = (i, key, value) =>
    setRows((previous) =>
      previous.map((row, idx) => {
        if (idx !== i) return row;
        if (["name", "room", "image", "unit"].includes(key)) {
          return { ...row, [key]: value };
        }
        const number = Number(value || 0);
        if (key === "quantity") {
          return {
            ...row,
            quantity: number,
            mrp: number * Number(row.rate || 0),
          };
        }
        if (key === "rate") {
          return {
            ...row,
            rate: number,
            mrp: number * Number(row.quantity ?? 1),
          };
        }
        return { ...row, [key]: number };
      }),
    );

  const allQuoteOptions = useMemo(
    () => [...quoteMasterOptions, ...masterItemsOptions],
    [quoteMasterOptions, masterItemsOptions],
  );

  const addSelectedQuoteItem = () => {
    if (!selectedItem || !selectedRoom) {
      alert("Please select both item and room.");
      return;
    }
    const quantity = Number(selectedQty);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      alert("Enter a quantity greater than zero.");
      return;
    }

    const selected = allQuoteOptions.find(
      (item) => item.value === selectedItem.value,
    );

    if (!selected) {
      alert("Selected item not found.");
      return;
    }

    setRows((prev) => [
      ...prev,
      {
        image: selected.image || "",
        name: selected.label || selected.value || "Custom Item",
        room: selectedRoom.value,
        unit: selectedUnit,
        quantity,
        rate: Number(selected.MRP || 0),
        length: Number(selected.length || 0),
        width: Number(selected.width || 0),
        mrp: Number(selected.MRP || 0) * quantity,
        isManual: false, // ✅ selected item image stays as it is; no upload option
      },
    ]);

    setSelectedItem(null);
    setSelectedRoom(null);
    setSelectedUnit("NOS");
    setSelectedQty(1);
  };

  const handleManualImageUpload = (rowIndex, file) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Please upload only image file.");
      return;
    }

    const reader = new FileReader();

    reader.onloadend = () => {
      setRows((prev) =>
        prev.map((row, idx) =>
          idx === rowIndex
            ? {
                ...row,
                image: reader.result || "",
              }
            : row,
        ),
      );
    };

    reader.readAsDataURL(file);
  };

  // Persist the optimized binary ON THE BACKEND, then keep ONLY metadata in state.
  // A 5MB file is never embedded as base64 into the MongoDB quotation.
  const handleDocumentUpload = async (fileList) => {
    const selected = Array.from(fileList || []);
    if (!selected.length) return;
    const allowed = new Set([
      "pdf",
      "doc",
      "docx",
      "xls",
      "xlsx",
      "jpg",
      "jpeg",
      "png",
    ]);
    if (documents.length + selected.length > MAX_DOCUMENT_COUNT) {
      alert(`Maximum ${MAX_DOCUMENT_COUNT} documents per quote.`);
      return;
    }
    if (selected.some((file) => !allowed.has(documentExtension(file.name)))) {
      alert("Only PDF, Word, Excel, JPG and PNG files are supported.");
      return;
    }

    setIsUploadingDocument(true);
    try {
      for (const file of selected) {
        const output = await optimizeDocument(file);
        const form = new FormData();
        form.append("file", output, output.name);
        // The compression route and upload route must target the same backend.
        const response = await axios.post(
          `${BASE_URL}/api/quotes/upload`,
          form,
          {
            timeout: 120000,
          },
        );
        const saved = response.data;
        if (!saved?.success || !saved?.url || !saved?.size) {
          throw new Error("Backend did not confirm a saved document.");
        }
        setDocuments((previous) => [
          ...previous,
          {
            name: saved.name || output.name,
            url: saved.url,
            type: saved.type || output.type,
            size: saved.size,
            originalSize: file.size,
            optimized: file.size > output.size,
          },
        ]);
      }
    } catch (error) {
      console.error("Backend document upload failed:", error);
      alert(
        error.response?.data?.message ||
          error.message ||
          "Document upload failed.",
      );
    } finally {
      setIsUploadingDocument(false);
    }
  };

  const removeDocument = (index) => {
    setDocuments((previous) => previous.filter((_, i) => i !== index));
  };

  // Fetch from backend and preview a Blob URL inside ERP (avoids about:blank).
  const openDocument = async (doc) => {
    const extension = documentExtension(doc?.name);
    if (!["pdf", "png", "jpg", "jpeg"].includes(extension)) {
      await downloadDocument(doc);
      return;
    }
    try {
      const location = getDocumentLocation(doc);
      let blob;
      if (location.data) {
        blob = documentBlobFromData(doc, location.data);
      } else {
        const response = await axios.get(location.url, {
          responseType: "blob",
        });
        blob = response.data;
        if (
          !(blob instanceof Blob) ||
          !blob.size ||
          blob.type.includes("json")
        ) {
          throw new Error("Document could not be loaded from the backend.");
        }
      }
      closeDocumentPreview();
      const url = URL.createObjectURL(blob);
      previewObjectUrlRef.current = url;
      setPreviewDocument({
        name: doc.name || "Document",
        url,
        type: extension === "pdf" ? "pdf" : "image",
        doc,
      });
    } catch (error) {
      console.error("Document preview failed:", error);
      alert(
        error.response?.data?.message ||
          error.message ||
          "Document preview failed.",
      );
    }
  };

  const downloadDocument = async (doc) => {
    try {
      const location = getDocumentLocation(doc);
      let blob;
      if (location.data) {
        blob = documentBlobFromData(doc, location.data);
      } else {
        const response = await axios.get(location.url, {
          responseType: "blob",
        });
        blob = response.data;
        if (!(blob instanceof Blob) || !blob.size) {
          throw new Error("Server returned an empty document.");
        }
      }
      saveAs(blob, doc.name || "document");
    } catch (error) {
      console.error("Document download failed:", error);
      alert(
        error.message ||
          "Download failed. Check the document URL or backend CORS settings.",
      );
    }
  };

  const formatQuoteOptionLabel = (option) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <strong>{option.label}</strong>
      <span style={{ fontSize: 12, color: "#6b7280" }}>
        {option.category || option.source || "Item"} | ₹{" "}
        {Number(option.MRP || 0).toLocaleString("en-IN")}
      </span>
    </div>
  );

  /* per-row derived values */
  const computedRows = useMemo(() => {
    return rows.map((r) => {
      const area = Number(r.length || 0) * Number(r.width || 0);
      const quantity = Number(r.quantity ?? 1);
      const rate = Number(r.rate ?? r.mrp ?? 0);
      const afterRow = quantity * rate;
      const discountedPrice = applyDiscount
        ? afterRow - (afterRow * Number(discountPercentage || 0)) / 100
        : afterRow;
      return {
        ...r,
        area,
        quantity,
        rate,
        mrp: afterRow,
        afterRow,
        discountedPrice,
      };
    });
  }, [rows, applyDiscount, discountPercentage]);

  /* PM Fee and Discount are each calculated independently on Article Value.
     Exclusive GST is added; inclusive GST is extracted, never added twice. */
  const totals = useMemo(() => {
    const round2 = (value) =>
      Math.round((Number(value) + Number.EPSILON) * 100) / 100;

    const totalMRP = round2(
      computedRows.reduce((sum, row) => sum + Number(row.mrp || 0), 0),
    );
    const discountPct = applyDiscount ? Number(discountPercentage || 0) : 0;
    const pmPct = applyPmFee ? Number(pmFeePercentage || 0) : 0;
    const gstPct = applyGst ? Number(gstPercentage || 0) : 0;

    // Both percentages use the ORIGINAL Article Value (not each other).
    const projectManagementFee = round2((totalMRP * pmPct) / 100);
    const discountAmount = round2((totalMRP * discountPct) / 100);

    const subtotal = round2(totalMRP + projectManagementFee);
    const afterDiscount = round2(subtotal - discountAmount);
    const beforeGst = afterDiscount;

    const gstAmount = !applyGst
      ? 0
      : gstMode === "inclusive"
        ? round2((beforeGst * gstPct) / (100 + gstPct))
        : round2((beforeGst * gstPct) / 100);

    const finalValue = round2(
      applyGst && gstMode === "exclusive" ? beforeGst + gstAmount : beforeGst,
    );
    const taxableValue = round2(finalValue - gstAmount);

    return {
      totalMRP,
      projectManagementFee,
      subtotal,
      discountAmount,
      afterDiscount,
      beforeGst,
      gstAmount,
      finalValue,
      taxableValue,
      discountPct,
      pmPct,
      gstPct,
    };
  }, [
    computedRows,
    applyDiscount,
    discountPercentage,
    applyPmFee,
    pmFeePercentage,
    applyGst,
    gstPercentage,
    gstMode,
  ]);

  // date (keep original when editing)
  const computedDate =
    isEdit && initialQuote?.date
      ? initialQuote.date
      : new Date().toISOString().split("T")[0];

  // payload for backend
  const buildPayload = () => {
    const items = computedRows.map((r) => ({
      label: r.name || "Custom Item",
      value: r.name || "Custom Item",
      description: "",
      room: r.room || "",
      unit: r.unit || "NOS",
      quantity: Number(r.quantity ?? 1),
      rate: Number(r.rate || 0),
      length: Number(r.length || 0),
      width: Number(r.width || 0),
      area: Number(r.area || 0),
      image: r.image || "",
      MRP: Math.round((Number(r.mrp || 0) + Number.EPSILON) * 100) / 100,
      discountPct: 0, // compatibility
      netAfterRow:
        Math.round((Number(r.mrp || 0) + Number.EPSILON) * 100) / 100,
      isManual: Boolean(r.isManual),
    }));

    return {
      clientName,
      clientAddress,
      items,
      totalMRP: totals.totalMRP,
      projectManagementFee: totals.projectManagementFee,
      projectManagementFeePercentage: applyPmFee
        ? Number(Number(pmFeePercentage || 0).toFixed(2))
        : 0,
      discountAmount: totals.discountAmount,
      discountPercentage: applyDiscount ? Number(discountPercentage || 0) : 0,
      gstAmount: totals.gstAmount,
      gstPercentage: applyGst ? Number(gstPercentage || 0) : 0,
      gstMode: applyGst ? gstMode : "exclusive",
      inclusiveGST: applyGst && gstMode === "inclusive",
      taxableValue: totals.taxableValue,
      documents: documents.map(
        ({ name, url, type, size, originalSize, optimized }) => ({
          name,
          url,
          type,
          size,
          originalSize,
          optimized,
        }),
      ),
      applyDiscount,
      applyProjectManagementFee: applyPmFee,
      applyGST: applyGst,
      finalAmount: totals.finalValue,
      date: computedDate,
      projectName: projectName || "Custom",
      status,
      custom: true,
      title: title || "Custom Quote",
      // keep this legacy field so old custom-quote detection still works
      globalDiscountPct: applyDiscount ? Number(discountPercentage || 0) : 0,
    };
  };

  // save / update (upsert)
  const handleSave = async () => {
    if (!clientName || rows.length === 0) {
      alert("Please enter client name and add at least one line item.");
      return;
    }
    if (
      rows.some(
        (row) =>
          !Number.isFinite(Number(row.quantity)) || Number(row.quantity) <= 0,
      )
    ) {
      alert("Each line must have quantity greater than zero.");
      return;
    }

    const payload = buildPayload();
    const id = initialQuoteId || initialQuote?._id;

    try {
      if (isEdit) {
        if (!id) {
          alert("Missing quote id for update.");
          return;
        }
        await axios.post(`${BASE_URL}/api/quotes/save`, {
          _id: id,
          ...payload,
        });
        alert("Custom quote updated!");
      } else {
        await axios.post(`${BASE_URL}/api/quotes/save`, payload);
        alert("Custom quote saved!");
      }
      navigate(-1);
    } catch (e) {
      console.error("Failed to save/update custom quote", {
        url: `${BASE_URL}/api/quotes/save`,
        status: e.response?.status,
        data: e.response?.data,
      });
      alert(
        `Failed. ${
          e.response?.data?.message ||
          e.response?.data?.error ||
          e.message ||
          "Check console for details."
        }`,
      );
    }
  };

  const handleSaveAsNew = async () => {
    const payload = {
      ...buildPayload(),
      date: new Date().toISOString().split("T")[0],
    };
    try {
      await axios.post(`${BASE_URL}/api/quotes/save`, payload);
      alert("Saved as new quote!");
      navigate(-1);
    } catch (e) {
      console.error("Failed to save as new", {
        status: e.response?.status,
        data: e.response?.data,
      });
      alert(
        `Failed to save as new. ${
          e.response?.data?.message || e.message || "Check console for details."
        }`,
      );
    }
  };

  /* ========================== EXPORT: PDF (your design) ========================== */
  const exportToPDF = async () => {
    // Map items from current rows
    const items = computedRows.map((r) => ({
      image: r.image || "",
      label: r.name || "",
      description: "", // keep empty (no per-item description here)
      room: r.room || "",
      unit: r.unit || "NOS",
      quantity: Number(r.quantity ?? 1),
      MRP: Math.round(Number(r.mrp || 0)),
    }));

    const totalMRP = Math.round(totals.totalMRP);
    const projectManagementFee = Math.round(totals.projectManagementFee);
    const discountAmount = Math.round(totals.discountAmount);
    const gstAmount = Math.round(totals.gstAmount);
    const finalValue = Math.round(totals.finalValue);

    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;

    const roomSummaryData = Object.values(
      items.reduce((acc, item) => {
        if (item.room) {
          const discountedPrice = applyDiscount
            ? item.MRP - (item.MRP * Number(discountPercentage || 0)) / 100
            : item.MRP;
          if (!acc[item.room]) {
            acc[item.room] = { roomName: item.room, cost: 0 };
          }
          acc[item.room].cost += discountedPrice || 0;
        }
        return acc;
      }, {}),
    );

    const drawFooter = () => {
      const footerHeight = 12;
      const footerY = pdfHeight - footerHeight;
      pdf.setFillColor(52, 58, 118);
      pdf.rect(0, footerY, pdfWidth, footerHeight, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(10);
      pdf.text(
        "SF 9 Time Square 2, Nr. Sindhu Bhavan Road, Thaltej, Ahmedabad-380059 | Mobile: 9998038595",
        pdfWidth / 2,
        pdfHeight - 4,
        { align: "center" },
      );
    };

    const loginUserName =
      JSON.parse(localStorage.getItem("user"))?.name || "Team SVB";

    const renderToPDFPages = async (div) => {
      const canvas = await html2canvas(div, {
        scale: 2,
        backgroundColor: "#d3d3d3",
        useCORS: true,
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.7);
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;
      const PAGE_EPSILON = 1;

      const drawGreyPage = () => {
        pdf.setFillColor(211, 211, 211);
        pdf.rect(0, 0, pdfWidth, pdfHeight, "F");
      };

      drawGreyPage();

      if (imgHeight <= pdfHeight + PAGE_EPSILON) {
        pdf.addImage(
          imgData,
          "JPEG",
          0,
          0,
          pdfWidth,
          Math.min(imgHeight, pdfHeight),
        );
        drawFooter();
        return;
      }

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "JPEG", 0, position, pdfWidth, imgHeight);
      drawFooter();
      heightLeft -= pdfHeight;

      while (heightLeft > PAGE_EPSILON) {
        position -= pdfHeight;
        pdf.addPage();
        drawGreyPage();
        pdf.addImage(imgData, "JPEG", 0, position, pdfWidth, imgHeight);
        drawFooter();
        heightLeft -= pdfHeight;
      }
    };

    // COVER PAGE
    const coverDiv = document.createElement("div");
    coverDiv.style.width = "210mm";
    coverDiv.style.height = "297mm";
    coverDiv.style.padding = "0";
    coverDiv.style.boxSizing = "border-box";
    coverDiv.style.fontFamily = "Calibri, sans-serif";
    coverDiv.style.backgroundColor = "#d3d3d3"; // << fixed '#'
    coverDiv.style.margin = "0";
    coverDiv.style.position = "relative";

    coverDiv.innerHTML = `
     <div style="width: 100%; display: flex; justify-content: space-between; align-items: center; padding: 15px 25px 10px 25px;">
       <div style="font-size: 26px; font-weight: bold; color: #343A76;">SOFTVOLUTE BUILDCON LLP</div>
       <div style="margin-right: 40px;">
         <img src="/logo.jpeg" alt="logo" style="width: 60px; height: auto;" />
       </div>
     </div>

     <div style="width: 100%; border-bottom: 2px solid #343A76; margin: 0 25px 30px 25px;"></div>

     <h1 style="text-align: center; color: #343A76; margin-top: 0px; font-size: 36px;">Site Service Quotation</h1>

     <div style="text-align: center; margin-top: 15px;">
       <div style="display: inline-block; padding: 6px 12px; background: white; color: #343A76; font-size: 16px; font-weight: 500; box-shadow: 0 5px 5px rgba(0,0,0,0.1);">
         Keep Your Costs On Track For Your New Home Interiors
       </div>
     </div>

     <div style="margin: 50px 25px 180px 25px; font-size: 16px; color: #343A76; line-height: 1.7;">
       Dear <strong>${clientName || "-"}</strong> & Family!<br/><br/>
       Please go through our estimated price quote for your project.<br/>
       It may upgrade as per your design elevates.<br/><br/>
       Tracking of budget & time can be crucial for this kind of works. But with SVB's transparent and informed systems, client stay aware of project updates.<br/><br/>
       Humans will be working at site so their craftmanship may take bit extra hours but quality assurance shall be confirmed from our side.<br/><br/>
       A Big Thank You for choosing SVB for your project & Welcome.<br/><br/>
       Let’s dive in!
     </div>

     <div style="display: flex; justify-content: space-between; align-items: flex-end; margin-top: 40px;">
       <div style="font-family: 'Lucida Calligraphy', cursive; font-size: 26px; color: #343A76; font-weight: normal; max-width: 50%; padding-left: 40px;">
         Home is the place where every corner tells your story ....
       </div>
       <div style="padding-right: 30px;">
         <img src="/relaxing img-02.jpg" style="width: 280px; max-height: 180px;" />
       </div>
     </div>
    `;

    // QUOTE TABLE PAGES (10 items/page)
    const quoteDivs = [];
    const itemsPerPage = 10;
    for (let i = 0; i < items.length; i += itemsPerPage) {
      const currentItems = items.slice(i, i + itemsPerPage);
      const quoteDiv = document.createElement("div");
      quoteDiv.style.width = "210mm";
      quoteDiv.style.minHeight = "297mm";
      quoteDiv.style.padding = `${margin}mm`;
      quoteDiv.style.fontFamily = "Calibri, sans-serif";
      quoteDiv.style.backgroundColor = "#d3d3d3";

      const showDiscount = applyDiscount && Number(discountPercentage || 0) > 0;

      quoteDiv.innerHTML = `
        <div style="font-size: 22px; font-weight: bold; color: #343A76;">SoftVolute Buildcon LLP</div>
        <div style="font-size: 14px;">
          Client: ${clientName || "-"}<br/>Address: ${clientAddress || "-"}
          <span style="float:right;">Date: ${new Date().toLocaleDateString(
            "en-IN",
          )}</span>
        </div>
        <h2 style="text-align:center; font-size: 20px; margin-top: 10px;">Quote Details</h2>

        <table style="width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 10px; background-color: #FFFFED; border: 0.5px solid black; border-radius: 8px; overflow: hidden;">
          <thead>
            <tr style="background-color: #707171; color: #FFFFED;">
              <th style="border: 0.5px solid lightgrey; padding: 8px;">Sr No</th>
              <th style="border: 0.5px solid lightgrey; padding: 8px;">Image</th>
              <th style="border: 0.5px solid lightgrey; padding: 8px;">Item Name</th>
              <th style="border: 0.5px solid lightgrey; padding: 5px;">Room</th>
              <th style="border: 0.5px solid lightgrey; padding: 5px;">Unit</th>
              <th style="border: 0.5px solid lightgrey; padding: 5px;">Qty</th>
              <th style="border: 0.5px solid lightgrey; padding: 5px; white-space: nowrap;">Amount<br/>(INR)</th>
              ${
                showDiscount
                  ? `<th style="border: 0.5px solid lightgrey; padding: 8px; white-space: nowrap;">Discounted Price</th>`
                  : ""
              }
            </tr>
          </thead>
          <tbody>
            ${currentItems
              .map((item, index) => {
                const discounted = Math.round(
                  item.MRP - (item.MRP * Number(discountPercentage || 0)) / 100,
                );
                const priceCell = showDiscount
                  ? `<span style="text-decoration: line-through; color: black; font-size: 15px; white-space: nowrap;">₹ ${item.MRP.toLocaleString(
                      "en-IN",
                    )}</span>`
                  : `<span style="color: black; font-size: 15px; white-space: nowrap;">₹ ${item.MRP.toLocaleString(
                      "en-IN",
                    )}</span>`;

                return `
                  <tr>
                    <td style="border: 0.5px solid lightgrey; text-align: center; padding: 8px;">${
                      i + index + 1
                    }</td>
                    <td style="border: 0.5px solid lightgrey; text-align: center; padding: 8px;">
                      ${
                        item.image
                          ? `<img src="${item.image}" style="width: 80px; height: 50px; margin: 2px; object-fit: cover;" />`
                          : ""
                      }
                    </td>
                    <td style="border: 0.5px solid lightgrey; padding: 8px;"><strong>${
                      item.label
                    }</strong><br/>${item.description || ""}</td>
                    <td style="border: 0.5px solid lightgrey; text-align: center; padding: 8px;">${
                      item.room
                    }</td>
                    <td style="border: 0.5px solid lightgrey; text-align: center; padding: 5px;">${item.unit}</td>
                    <td style="border: 0.5px solid lightgrey; text-align: center; padding: 5px;">${item.quantity}</td>
                    <td style="border: 0.5px solid lightgrey; text-align: right; padding: 5px; white-space: nowrap;">${priceCell}</td>
                    ${
                      showDiscount
                        ? `<td style="border: 0.5px solid lightgrey; text-align: right; padding: 8px; color: green; font-weight: bold; white-space: nowrap;">₹ ${discounted.toLocaleString(
                            "en-IN",
                          )}</td>`
                        : ""
                    }
                  </tr>
                `;
              })
              .join("")}
          </tbody>
        </table>
      `;
      quoteDivs.push(quoteDiv);
    }

    // COST SUMMARY PAGE — same optional charge blocks as Quotes.jsx
    const discountBlock =
      applyDiscount && discountAmount > 0 && Number(discountPercentage || 0) > 0
        ? `
        <div style="display: flex; justify-content: space-between; font-size: 20px; margin-bottom: 10px;">
          <span style="color: green; font-weight: bold;">Discount on Article Value (${Number(discountPercentage || 0)}%):</span>
          <span style="color: green; font-weight: bold;">-₹ ${Math.round(
            discountAmount,
          ).toLocaleString("en-IN")}</span>
        </div>
        <hr style="border-top: 1.2px solid #6f4e37; margin: 0 0 10px 0;" />
        `
        : "";

    const pmFeeBlock =
      applyPmFee && projectManagementFee > 0
        ? `
        <div style="display: flex; justify-content: space-between; font-size: 20px; margin-bottom: 10px;">
          <span>Project Management & Design Fees on Article Value (${Number(Number(pmFeePercentage || 0).toFixed(2))}%)</span>
          <span>₹ ${Math.round(projectManagementFee).toLocaleString("en-IN")}</span>
        </div>
        <hr style="border-top: 1.2px solid #6f4e37; margin: 0 0 10px 0;" />
        `
        : "";

    const gstBlock =
      applyGst && gstAmount > 0
        ? `
        <div style="display: flex; justify-content: space-between; font-size: 20px; margin-bottom: 10px;">
          <span>${gstMode === "inclusive" ? "Inclusive GST (included, not added)" : "Exclusive GST"} (${Number(Number(gstPercentage || 0).toFixed(2))}%)</span>
          <span>₹ ${Math.round(gstAmount).toLocaleString("en-IN")}</span>
        </div>
        <hr style="border-top: 1.2px solid #6f4e37; margin: 0 0 10px 0;" />
        `
        : "";

    const costDiv = document.createElement("div");
    costDiv.style.width = "210mm";
    costDiv.style.minHeight = "297mm";
    costDiv.style.padding = `${margin}mm`;
    costDiv.style.fontFamily = "Calibri, sans-serif";
    costDiv.style.backgroundColor = "#d3d3d3";

    costDiv.innerHTML = `
      <div style="font-size: 22px; font-weight: bold; color: #343A76;">SoftVolute Buildcon LLP</div>
      <div style="font-size: 14px; margin-bottom: 10px;">
        Client: ${clientName || "-"}<br/>Address: ${clientAddress || "-"}
        <span style="float:right;">Date: ${new Date().toLocaleDateString(
          "en-IN",
        )}</span>
      </div>

      <h2 style="text-align: center; font-size: 26px; font-weight: bold; color: #343A76; margin-top: 60px; margin-bottom: 30px;">
        Total Cost Breakup<br/>(Site Service)
      </h2>

      <div style="background-color: #fff3b0; border-radius: 25px; padding: 25px 35px; width: 80%; margin: 0 auto; box-shadow: 5px 8px 12px rgba(0, 0, 0, 0.08);">
        <div style="display: flex; justify-content: space-between; font-size: 20px; margin-bottom: 10px;">
          <span>Total Article Value</span>
          <span>₹ ${Math.round(totalMRP).toLocaleString("en-IN")}</span>
        </div>
        <hr style="border-top: 1.2px solid #6f4e37; margin: 0 0 10px 0;" />

        ${pmFeeBlock}
        ${discountBlock}
        ${gstBlock}

        <div style="display: flex; justify-content: space-between; font-size: 22px; font-weight: bold; color: #343A76;">
          <span>Total</span>
          <span>₹ ${Math.round(finalValue).toLocaleString("en-IN")}</span>
        </div>
        <hr style="border-top: 1.5px solid #6f4e37; margin: 10px 0 15px 0;" />

        <div style="font-size: 14px; color: #6f4e37; margin-top: 15px;">
          ${
            applyGst
              ? gstMode === "inclusive"
                ? "* Inclusive GST is already part of Total and is not added again.<br/>"
                : "* GST has been added to the amount above.<br/>"
              : ""
          }
          * This quote will expire on
          <strong>${new Date(
            Date.now() + 30 * 24 * 60 * 60 * 1000,
          ).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "long",
            year: "numeric",
          })}</strong>
        </div>

        <div style="text-align: right; margin-top: 30px; font-size: 20px; color: #343A76;">
          <strong>Prepared by:</strong> ${loginUserName}
          <br/>
          <span style="color: grey; font-size: 14px;">(If you have any questions about the quotation, feel free to reach out to your designer.)</span>
        </div>
      </div>
    `;

    // ROOM SUMMARY PAGE
    const createRoomSummaryDiv = () => {
      const div = document.createElement("div");
      div.style.width = "210mm";
      div.style.minHeight = "297mm";
      div.style.padding = "15mm";
      div.style.fontFamily = "Calibri, sans-serif";
      div.style.backgroundColor = "#d3d3d3";

      div.innerHTML = `
        <div style="font-size: 22px; font-weight: bold; color: #343A76;">SoftVolute Buildcon LLP</div>
        <div style="font-size: 14px; margin-bottom: 10px;">
          Client: ${clientName || "-"}<br/>Address: ${clientAddress || "-"}
          <span style="float:right;">Date: ${new Date().toLocaleDateString(
            "en-IN",
          )}</span>
        </div>

        <div style="font-size: 26px; font-weight: bold; color: #343A76; margin-bottom: 8px; margin-top: 30px; text-align: center;">
          <span style="color: #343A76; padding-left: 10px;">ROOM SUMMARY</span>
        </div>

        <div style="font-size: 16px; font-style: italic; color: #343A76; margin-bottom: 30px; text-align: center;">
          The Sweetest Memories Start From Here. <br/> And here's what they cost
        </div>

        <div style="width: 100%; border-top: 2px solid #343A76; margin: 20px 0;"></div>

        <div style="background-color: #fff3b0; border-radius: 15px; box-shadow: 0px 12px 20px rgba(0,0,0,0.2); padding: 25px; width: 90%; margin: auto;">
          <table style="width: 100%; border-collapse: collapse; font-size: 14px; color: #5a4a42;">
            <thead>
              <tr style="border-bottom: 1px solid #6f4e37;">
                <th style="text-align: left; padding: 8px;">SL No</th>
                <th style="text-align: left; padding: 8px;">Room Name</th>
                <th style="text-align: right; padding: 8px;">Cost Per Room</th>
              </tr>
            </thead>
            <tbody>
              ${roomSummaryData
                .map(
                  (room, index) => `
                    <tr style="border-bottom: 1px solid #d3d3d3;">
                      <td style="padding: 8px;">${index + 1}</td>
                      <td style="padding: 8px;">${room.roomName}</td>
                      <td style="padding: 8px; text-align: right;">₹ ${Math.round(
                        room.cost,
                      ).toLocaleString("en-IN")}</td>
                    </tr>
                  `,
                )
                .join("")}
            </tbody>
          </table>
        </div>
      `;
      return div;
    };

    // NOTES PAGE
    const createStyledNotesDiv = () => {
      const div = document.createElement("div");
      div.style.width = "210mm";
      div.style.minHeight = "297mm";
      div.style.padding = "15mm";
      div.style.fontFamily = "Calibri, sans-serif";
      div.style.fontSize = "14px";
      div.style.lineHeight = "1.5";
      div.style.backgroundColor = "#d3d3d3";

      div.innerHTML = `
        <h2 style="font-size: 20px; margin-bottom: 10px;">Notes</h2>
        <div style="background-color: #fff3b0; border: 1px solid #000; padding: 15px; border-radius: 15px;">
          <ul style="list-style-type: disc; padding-left: 20px; margin: 0;">
            <li><strong>Payment Stages:</strong><br/>1. Stage 1: 10% Booking amount.<br/>2. Stage 2: 50% Before Starting of project.<br/>3. Stage 3: 40% Before finishing stage.</li><br/>
            <li><strong>Material:</strong><br/>1. Plywood - ISI grade 303 - Red and White.<br/>2. Decorative Laminate will be used of maximum of 1500 Rs/Sheet.<br/>3. Light fitting as per standard make with 2 years of warranty<br/>4. Paint - Asian paint Royal or similar. <br/>5. Wire-RR or Finolex as per standard ISI graded.</li><br/>
            <li><strong>General Condition:</strong><br/>1. Any payment made is non-refundable.<br/>2. Initial quote is based on primary discussion, after design stage & site visit quote may vary as per design revisions & site conditions.<br/>3. Item brands may vary as per product availability.<br/>4. Electricity required for site work should be provided by owner.<br/>5. Quotation is valid for 1 month only.<br/>6. Society approvals, municipal approvals, structural approvals, and Fire approvals are in client scope only.<br/>7. Softvolute Buildcon LLP not responsible for any legal approvals required for this work.<br/>8. If work get hold during execution for any reason, quotation is subject to revise.<br/>9. Material storage space needs to be provided by owners. Material storage space should be within working premises, so it ensures safety.<br/>10. Appliances are in client scope.</li><br/>
            <li><strong>Warranty:</strong><br/>1. The Product warranty is - As per manufacturer warranty<br/>2. Bed hydraulic mechanism with 2 years of warranty as per manufacturer policy<br/>3. General repair warranty is for 2 years, but if found broken or damaged purposely, an extra charge will be imposed.<br/>4. After completion, the warranty period starts. For examination visits, a charge of Rs.500 + GST must be paid in advance.</li><br/>
            <li><strong>Payment Charges:</strong><br/>1. Payment from <strong>credit card – 2.9%</strong> bank charges shall be extra, payment <strong>via link – 2.5%</strong> bank charges extra, and payment from <strong>debit card – 1.9%</strong> bank charges.<br/>2. Payment via QR code, UPI, NEFT and RTGS will not attract any extra charges.</li>
          </ul>
        </div>
      `;
      return div;
    };

    // BANK DETAILS / SIGN PAGE
    const createNotesFooterDiv = () => {
      const div = document.createElement("div");
      div.style.width = "210mm";
      div.style.minHeight = "297mm";
      div.style.padding = "15mm";
      div.style.fontFamily = "Calibri, sans-serif";
      div.style.fontSize = "14px";
      div.style.lineHeight = "1.5";
      div.style.backgroundColor = "#d3d3d3";

      div.innerHTML = `
        <div style="margin-top: 40px; font-size: 14px;">
          <div style="display: flex; justify-content: space-between;">
            <div>
              <strong>Bank Details</strong><br/>
              Account Name: SOFTVOLUTE BUILDCON LLP <br/>
              Bank: DBS BANK<br/>
              A/C No: 8831210000001915<br/>
              IFSC: DBSS0IN0831
            </div>

            <div style="display: flex; gap: 20px; align-items: center;">
              <img src="/QRCOde.jpg" style="width: 120px;" alt="QR Code" />
              <img src="/IndQr.jpg" style="width: 120px;" alt="QR Code" />
            </div>
          </div>

          <div style="margin-top: 20px;">
            <div style="border-bottom: 1px solid black; width: 200px; margin: 8px 0;"></div>
            <strong>Customer Sign & Date</strong><br/>
            <span style="font-size: 12px;">(Email confirmation also accepted)</span>
          </div>

          <div style="text-align: right; margin-top: 30px; font-size: 20px; color: #343A76;">
            <strong>Prepared by:</strong> ${loginUserName}
          </div>
        </div>
      `;
      return div;
    };

    // Render all parts — each section starts on a fresh PDF page, same as Quotes.jsx
    document.body.appendChild(coverDiv);
    await renderToPDFPages(coverDiv);
    document.body.removeChild(coverDiv);

    for (let i = 0; i < quoteDivs.length; i++) {
      pdf.addPage();
      const div = quoteDivs[i];
      document.body.appendChild(div);
      await renderToPDFPages(div);
      document.body.removeChild(div);
    }

    pdf.addPage();
    document.body.appendChild(costDiv);
    await renderToPDFPages(costDiv);
    document.body.removeChild(costDiv);

    pdf.addPage();
    const roomSummaryDiv = createRoomSummaryDiv();
    document.body.appendChild(roomSummaryDiv);
    await renderToPDFPages(roomSummaryDiv);
    document.body.removeChild(roomSummaryDiv);

    pdf.addPage();
    const notesDivFinal = createStyledNotesDiv();
    document.body.appendChild(notesDivFinal);
    await renderToPDFPages(notesDivFinal);
    document.body.removeChild(notesDivFinal);

    pdf.addPage();
    const notesFooterDiv = createNotesFooterDiv();
    document.body.appendChild(notesFooterDiv);
    await renderToPDFPages(notesFooterDiv);
    document.body.removeChild(notesFooterDiv);

    pdf.save(
      `${title?.trim() || "Quote"}-${projectName?.trim() || "Project"}.pdf`,
    );
  };

  /* ========================== EXPORT: EXCEL (matching design) ========================== */
  const exportQuoteTableToExcel = async () => {
    const items = computedRows.map((r) => ({
      label: r.name || "",
      description: "",
      room: r.room || "",
      unit: r.unit || "NOS",
      quantity: Number(r.quantity ?? 1),
      MRP: Math.round(Number(r.mrp || 0)),
    }));

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Quote");

    // 1. Company Header
    worksheet.mergeCells("A1:G1");
    worksheet.getCell("A1").value = "SoftVolute Buildcon LLP";
    worksheet.getCell("A1").font = {
      bold: true,
      size: 18,
      color: { argb: "FF343A76" },
    };
    worksheet.getCell("A1").alignment = {
      vertical: "middle",
      horizontal: "left",
    };

    worksheet.getCell("A2").value = `Client: ${clientName || ""}`;
    worksheet.getCell("A3").value = `Address: ${clientAddress || ""}`;
    worksheet.getCell("G2").value = `Date: ${new Date().toLocaleDateString(
      "en-IN",
    )}`;
    worksheet.getCell("G2").alignment = { horizontal: "right" };

    // 2. Section Title
    worksheet.mergeCells("A5:G5");
    worksheet.getCell("A5").value = "Quote Details";
    worksheet.getCell("A5").font = { bold: true, size: 16 };
    worksheet.getCell("A5").alignment = { horizontal: "center" };

    // 3. Table Headers
    const headerRow = worksheet.addRow([
      "Sr No",
      "Image",
      "Item Name",
      "Room",
      "Unit",
      "Qty",
      "Amount (INR)",
    ]);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF707171" },
      };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border = {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // 4. Table Data
    items.forEach((item, index) => {
      const row = worksheet.addRow([
        index + 1,
        "", // image cell (kept blank)
        (item.label || "") + (item.description ? `\n${item.description}` : ""),
        item.room || "",
        item.unit || "NOS",
        Number(item.quantity ?? 1),
        item.MRP || 0,
      ]);
      row.height = 60;

      row.getCell(3).alignment = {
        vertical: "top",
        horizontal: "left",
        wrapText: true,
      };
      row.getCell(1).alignment = { vertical: "middle", horizontal: "center" };
      row.getCell(2).alignment = { vertical: "middle", horizontal: "center" };
      row.getCell(4).alignment = { vertical: "middle", horizontal: "center" };
      row.getCell(5).alignment = { vertical: "middle", horizontal: "center" };
      row.getCell(6).alignment = { vertical: "middle", horizontal: "center" };
      row.getCell(7).alignment = { vertical: "middle", horizontal: "right" };
    });

    // 5. Column widths
    worksheet.columns = [
      { width: 8 }, // Sr No
      { width: 20 }, // Image
      { width: 60 }, // Item Name
      { width: 20 }, // Room
      { width: 10 }, // Unit
      { width: 10 }, // Qty
      { width: 17 }, // Amount
    ];

    // Gap
    worksheet.addRow([]);

    // 6. Total Cost Breakup (matching PDF)
    worksheet.addRow([]);
    worksheet.mergeCells(
      `A${worksheet.lastRow.number + 1}:G${worksheet.lastRow.number + 1}`,
    );
    const totalBreakupHeading = worksheet.getRow(worksheet.lastRow.number);
    totalBreakupHeading.getCell(1).value = "Total Cost Breakup (Site Service)";
    totalBreakupHeading.font = {
      bold: true,
      size: 16,
      color: { argb: "FF343A76" },
    };
    totalBreakupHeading.alignment = {
      horizontal: "center",
      vertical: "middle",
    };

    worksheet.addRow([]);

    const priceSummary = [
      ["Total Article Value", Math.round(totals.totalMRP) || 0],
      ...(applyPmFee
        ? [
            [
              `PM Fee on Article Value (${Number(Number(pmFeePercentage || 0).toFixed(2))}%)`,
              Math.round(totals.projectManagementFee || 0),
            ],
          ]
        : []),
      ...(applyDiscount
        ? [
            [
              `Discount on Article Value (${Number(discountPercentage || 0)}%)`,
              -Math.round(totals.discountAmount || 0),
            ],
          ]
        : []),
      ...(applyGst
        ? [
            [
              `${gstMode === "inclusive" ? "Inclusive GST (included; not added)" : "Exclusive GST"} (${Number(Number(gstPercentage || 0).toFixed(2))}%)`,
              Math.round(totals.gstAmount || 0),
            ],
          ]
        : []),
      ["Total", Math.round(totals.finalValue || 0)],
    ];

    priceSummary.forEach(([label, value], idx) => {
      const row = worksheet.addRow([label, "", "", "", "", "", value]);
      worksheet.mergeCells(`A${row.number}:F${row.number}`);

      row.getCell(1).alignment = { horizontal: "left", vertical: "middle" };
      row.getCell(7).alignment = { horizontal: "right", vertical: "middle" };

      row.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFFFFACD" },
        };
        cell.border = {
          top: { style: "thin" },
          bottom: { style: "thin" },
          left: { style: "thin" },
          right: { style: "thin" },
        };
      });

      row.getCell(1).font = { bold: true };
      row.getCell(7).font = { bold: true };

      if (label.startsWith("Discount on Article Value")) {
        row.getCell(1).font = { bold: true, color: { argb: "FF008000" } };
        row.getCell(7).font = { bold: true, color: { argb: "FF008000" } };
      }
      if (label === "Total") {
        row.getCell(1).font = { bold: true, color: { argb: "FF343A76" } };
        row.getCell(7).font = { bold: true, color: { argb: "FF343A76" } };
      }
    });

    // gaps
    worksheet.addRow([]);
    worksheet.addRow([]);
    worksheet.addRow([]);

    // 7. Notes (same list as PDF)
    const notesData = [
      "* Payment Stages:",
      "1. Stage 1: 10% Booking amount.",
      "2. Stage 2: 50% Before Starting of project.",
      "3. Stage 3: 40% Before finishing stage.",
      "",
      "* Material:",
      "1. Plywood - ISI grade 303 - Red and White.",
      "2. Decorative Laminate 1500 Rs/Sheet",
      "3. Light fitting ISI mark (2 yrs warranty)",
      "4. Paint - Asian paint royal/similar.",
      "5. Wire - RR or Finolex ISI grade",
      "",
      "* General Conditions:",
      "1. Any payment is non-refundable.",
      "2. Design stage and site visit may change quote.",
      "3. Item brands may vary.",
      "4. Electricity to be provided by owner.",
      "5. Quotation valid for 1 month.",
      "",
      "* Warranty:",
      "1. Product warranty as per manufacturer.",
      "2. Hydraulic mechanism 2 years warranty.",
      "3. General repair warranty 2 years.",
      "",
      "* Payment Charges:",
      "1. Credit card 2.9%, Link 2.5%, Debit card 1.9%",
      "2. QR/UPI/NEFT/RTGS no extra charges.",
    ];
    notesData.forEach((text) => {
      const row = worksheet.addRow([text]);
      worksheet.mergeCells(`A${row.number}:G${row.number}`);
      row.getCell(1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFFFACD" },
      };
      row.getCell(1).alignment = {
        vertical: "top",
        horizontal: "left",
        wrapText: true,
      };
      row.height = 20;
    });

    // gaps
    worksheet.addRow([]);
    worksheet.addRow([]);
    worksheet.addRow([]);

    // 8. Bank details + sign section
    const bankDetails = [
      "Bank Details",
      "Account Name: SOFTVOLUTE BUILDCON LLP",
      "Bank: DBS BANK",
      "A/C No: 883112000001915",
      "IFSC: DBSS0IN0831",
    ];
    bankDetails.forEach((text, idx) => {
      const row = worksheet.addRow([text]);
      worksheet.mergeCells(`A${row.number}:C${row.number}`);
      row.getCell(1).alignment = { horizontal: "left", vertical: "middle" };
      row.getCell(1).font = { bold: idx === 0, size: idx === 0 ? 12 : 11 };
    });

    worksheet.addRow([]);
    worksheet.addRow([]);

    const signRow = worksheet.addRow(["Customer Sign & Date"]);
    worksheet.mergeCells(`A${signRow.number}:C${signRow.number}`);
    signRow.getCell(1).font = { bold: true };
    signRow.getCell(1).alignment = { horizontal: "left", vertical: "middle" };

    const emailRow = worksheet.addRow(["(Email confirmation also accepted)"]);
    worksheet.mergeCells(`A${emailRow.number}:C${emailRow.number}`);
    emailRow.getCell(1).font = { italic: true, size: 10 };
    emailRow.getCell(1).alignment = { horizontal: "left", vertical: "middle" };

    // Save Excel
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/octet-stream" });
    saveAs(
      blob,
      `${title?.trim() || "Quote"}-${projectName?.trim() || "Project"}.xlsx`,
    );
  };

  /* ================================ UI ================================ */
  return (
    <div className="customer-quote-page">
      <div className="customer-quote-header">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="customer-quote-btn customer-quote-btn-back"
        >
          ← Back
        </button>
        <div>
          <h2>{isEdit ? "Edit Customer Quote" : "Create Customer Quote"}</h2>
          <p>
            Prepare a detailed customer quotation with items, charges and
            exports.
          </p>
        </div>
      </div>

      <section className="customer-quote-card customer-quote-info-card">
        <div className="customer-quote-section-heading">
          <div>
            <h3>Basic Information</h3>
            <span>Quote, project and client details</span>
          </div>
        </div>

        <div className="customer-quote-form-grid">
          <LabeledInput label="Quote Title" value={title} onChange={setTitle} />
          <LabeledInput
            label="Project Name"
            value={projectName}
            onChange={setProjectName}
          />
          <LabeledInput
            label="Client Name"
            value={clientName}
            onChange={setClientName}
          />
          <LabeledInput
            label="Client Address"
            value={clientAddress}
            onChange={setClientAddress}
          />
        </div>
      </section>

      <section className="customer-quote-card customer-quote-items-card">
        <div className="customer-quote-section-heading">
          <div>
            <h3>Quote Items</h3>
            <span>Add items from master or enter a custom line manually</span>
          </div>
        </div>

        <div className="customer-quote-selector-bar">
          <div className="customer-quote-selector customer-quote-selector-wide">
            <label className="customer-quote-label">
              Select Quote / Master Item
            </label>
            <Select
              options={allQuoteOptions}
              value={selectedItem}
              onChange={setSelectedItem}
              placeholder="Search item from Quote Master / Master Items"
              isSearchable
              formatOptionLabel={formatQuoteOptionLabel}
              classNamePrefix="customer-quote-select"
            />
          </div>

          <div className="customer-quote-selector customer-quote-selector-room">
            <label className="customer-quote-label">Room</label>
            <Select
              options={roomOptions.map((room) => ({
                label: room,
                value: room,
              }))}
              value={selectedRoom}
              onChange={setSelectedRoom}
              placeholder="Select Room"
              isSearchable
              classNamePrefix="customer-quote-select"
            />
          </div>

          <div className="customer-quote-selector customer-quote-selector-unit">
            <label className="customer-quote-label">Unit</label>
            <select
              className="customer-quote-input"
              value={selectedUnit}
              onChange={(event) => setSelectedUnit(event.target.value)}
            >
              <option value="QNT">QNT</option>
              <option value="NOS">NOS</option>
              <option value="SQ.FT">SQ.FT</option>
            </select>
          </div>

          <div className="customer-quote-selector customer-quote-selector-qty">
            <label className="customer-quote-label">Qty / Area</label>
            <input
              type="number"
              min="0.01"
              step="any"
              className="customer-quote-input"
              value={selectedQty}
              onChange={(event) => setSelectedQty(event.target.value)}
            />
          </div>

          <button
            type="button"
            onClick={addSelectedQuoteItem}
            className="customer-quote-btn customer-quote-btn-primary customer-quote-add-selected"
          >
            + Add Selected Item
          </button>
        </div>

        <div className="customer-quote-table-wrap">
          <table className="customer-quote-table">
            <thead>
              <tr>
                <th>Sr No</th>
                <th>Image</th>
                <th>Item Name</th>
                <th>Room</th>
                <th>Unit</th>
                <th>Qty</th>
                <th>Length</th>
                <th>Width</th>
                <th>Area</th>
                <th>Rate / Amount (INR)</th>
                {applyDiscount && <th>Discounted Price</th>}
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {computedRows.map((r, i) => (
                <tr key={i}>
                  <td className="customer-quote-center">{i + 1}</td>

                  <td className="customer-quote-image-cell">
                    {r.image ? (
                      <img
                        src={r.image}
                        alt={r.name || "item"}
                        className="customer-quote-item-image"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <span className="customer-quote-no-image">No image</span>
                    )}

                    {(isEdit || r.isManual) && (
                      <div className="customer-quote-upload-wrap">
                        <input
                          id={`manual-image-upload-${i}`}
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            handleManualImageUpload(i, e.target.files?.[0]);
                            e.target.value = "";
                          }}
                          className="customer-quote-hidden-input"
                        />

                        <button
                          type="button"
                          onClick={() =>
                            document
                              .getElementById(`manual-image-upload-${i}`)
                              ?.click()
                          }
                          className="customer-quote-upload-btn"
                        >
                          {r.image ? "Change Image" : "Upload Image"}
                        </button>
                      </div>
                    )}
                  </td>

                  <td>
                    <input
                      value={r.name}
                      onChange={(e) => setRow(i, "name", e.target.value)}
                      className="customer-quote-cell-input"
                      placeholder="Item name"
                    />
                  </td>

                  <td>
                    <select
                      value={r.room}
                      onChange={(e) => setRow(i, "room", e.target.value)}
                      className="customer-quote-cell-input"
                    >
                      <option value="">Select Room</option>
                      {roomOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </td>

                  <td>
                    <select
                      value={r.unit || "NOS"}
                      onChange={(event) =>
                        setRow(i, "unit", event.target.value)
                      }
                      className="customer-quote-cell-input customer-quote-unit-cell"
                    >
                      <option value="QNT">QNT</option>
                      <option value="NOS">NOS</option>
                      <option value="SQ.FT">SQ.FT</option>
                    </select>
                  </td>

                  <td>
                    <input
                      type="number"
                      min="0.01"
                      step="any"
                      value={rows[i].quantity ?? 1}
                      onChange={(event) =>
                        setRow(i, "quantity", event.target.value)
                      }
                      className="customer-quote-cell-input customer-quote-qty-cell"
                    />
                  </td>

                  <td>
                    <input
                      type="number"
                      min="0"
                      value={rows[i].length}
                      onChange={(e) => setRow(i, "length", e.target.value)}
                      className="customer-quote-cell-input customer-quote-number-input"
                      placeholder="0"
                    />
                  </td>

                  <td>
                    <input
                      type="number"
                      min="0"
                      value={rows[i].width}
                      onChange={(e) => setRow(i, "width", e.target.value)}
                      className="customer-quote-cell-input customer-quote-number-input"
                      placeholder="0"
                    />
                  </td>

                  <td className="customer-quote-number customer-quote-area-value">
                    {Math.round(r.area || 0).toLocaleString("en-IN")}
                  </td>

                  <td className="customer-quote-rate-cell">
                    <input
                      type="number"
                      min="0"
                      step="any"
                      value={rows[i].rate ?? rows[i].mrp ?? 0}
                      onChange={(e) => setRow(i, "rate", e.target.value)}
                      className="customer-quote-cell-input customer-quote-number-input"
                      placeholder="Rate"
                      title="Rate per unit"
                    />
                    <span className="customer-quote-line-total">
                      Total: ₹
                      {Number(r.mrp || 0).toLocaleString("en-IN", {
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </td>

                  {applyDiscount && (
                    <td className="customer-quote-number customer-quote-discounted-price">
                      ₹{" "}
                      {Math.round(r.discountedPrice || 0).toLocaleString(
                        "en-IN",
                      )}
                    </td>
                  )}

                  <td className="customer-quote-center">
                    <button
                      type="button"
                      onClick={() => removeRow(i)}
                      className="customer-quote-delete-btn"
                      aria-label="Remove line"
                      title="Remove line"
                    >
                      <FiTrash2 size={17} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="customer-quote-table-actions">
          <button
            type="button"
            onClick={addRow}
            className="customer-quote-btn customer-quote-btn-outline"
          >
            + Add Line
          </button>
        </div>
      </section>

      <section className="customer-quote-card customer-quote-summary-section">
        <div className="customer-quote-section-heading customer-quote-summary-heading">
          <div>
            <h3>Quote Summary &amp; Charges</h3>
            <span>Apply only the charges required for this quotation</span>
          </div>
        </div>

        {/* Six compact cards in ONE row. On narrow screens only this row scrolls. */}
        <div className="customer-quote-summary-scroll">
          <div className="customer-quote-summary-grid">
            {/* 1. ARTICLE VALUE */}
            <div className="customer-quote-summary-card customer-quote-article-card">
              <span className="customer-quote-summary-label">
                Article Value
              </span>
              <strong>
                ₹ {Math.round(totals.totalMRP).toLocaleString("en-IN")}
              </strong>
            </div>

            {/* 2. DISCOUNT */}
            <div
              className={`customer-quote-summary-card customer-quote-charge-card ${applyDiscount ? "is-active" : ""}`}
            >
              <label className="customer-quote-charge-toggle">
                <input
                  type="checkbox"
                  checked={applyDiscount}
                  onChange={(e) => setApplyDiscount(e.target.checked)}
                />
                <span>Apply Discount</span>
              </label>
              <div className="customer-quote-percent-wrap">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  aria-label="Discount percentage"
                  disabled={!applyDiscount}
                  value={discountPercentage}
                  onChange={(e) =>
                    setDiscountPercentage(
                      Math.max(0, Math.min(100, Number(e.target.value || 0))),
                    )
                  }
                />
                <span>%</span>
              </div>
              <small>Discount Amount</small>
              <strong className="customer-quote-discount-amount">
                - ₹ {Math.round(totals.discountAmount).toLocaleString("en-IN")}
              </strong>
            </div>

            {/* 3. PM FEE - original ARTICLE VALUE */}
            <div
              className={`customer-quote-summary-card customer-quote-charge-card ${applyPmFee ? "is-active" : ""}`}
            >
              <label className="customer-quote-charge-toggle">
                <input
                  type="checkbox"
                  checked={applyPmFee}
                  onChange={(e) => setApplyPmFee(e.target.checked)}
                />
                <span>Add Project Management Fee</span>
              </label>
              <div className="customer-quote-percent-wrap">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  aria-label="Project management fee percentage"
                  disabled={!applyPmFee}
                  value={pmFeePercentage}
                  onChange={(e) =>
                    setPmFeePercentage(
                      Math.max(0, Math.min(100, Number(e.target.value || 0))),
                    )
                  }
                />
                <span>%</span>
              </div>
              <small>PM Fee (on Article Value)</small>
              <strong>
                ₹{" "}
                {Math.round(totals.projectManagementFee).toLocaleString(
                  "en-IN",
                )}
              </strong>
            </div>

            {/* 4. EXCLUSIVE / INCLUSIVE GST */}
            <div
              className={`customer-quote-summary-card customer-quote-charge-card customer-quote-gst-card ${applyGst ? "is-active" : ""}`}
            >
              <label className="customer-quote-charge-toggle">
                <input
                  type="checkbox"
                  checked={applyGst}
                  onChange={(e) => setApplyGst(e.target.checked)}
                />
                <span>Add GST</span>
              </label>
              <select
                aria-label="GST calculation type"
                value={gstMode}
                disabled={!applyGst}
                onChange={(e) => setGstMode(e.target.value)}
                className="customer-quote-gst-select"
              >
                <option value="exclusive">Exclusive GST</option>
                <option value="inclusive">Inclusive GST</option>
              </select>
              <div className="customer-quote-percent-wrap">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  aria-label="GST percentage"
                  disabled={!applyGst}
                  value={gstPercentage}
                  onChange={(e) =>
                    setGstPercentage(
                      Math.max(0, Math.min(100, Number(e.target.value || 0))),
                    )
                  }
                />
                <span>%</span>
              </div>
              <small>
                {applyGst && gstMode === "inclusive"
                  ? "GST included (not added)"
                  : "GST Amount"}
              </small>
              <strong>
                ₹ {Math.round(totals.gstAmount).toLocaleString("en-IN")}
              </strong>
            </div>

            {/* 5. FINAL VALUE */}
            <div className="customer-quote-summary-card customer-quote-final-card">
              <span className="customer-quote-summary-label">Final Value</span>
              <strong>
                ₹ {Math.round(totals.finalValue).toLocaleString("en-IN")}
              </strong>
              {applyGst && gstMode === "inclusive" && (
                <small>Inclusive of GST</small>
              )}
            </div>

            {/* 6. DOCUMENTS: stays to the RIGHT of Final Value */}
            <div className="customer-quote-summary-card customer-quote-documents-card">
              <span className="customer-quote-summary-label">Documents</span>
              <input
                id="customer-quote-document-upload"
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                multiple
                className="customer-quote-hidden-input"
                onChange={(e) => {
                  handleDocumentUpload(e.target.files);
                  e.target.value = "";
                }}
              />
              <button
                type="button"
                disabled={isUploadingDocument || documents.length >= 3}
                onClick={() =>
                  document
                    .getElementById("customer-quote-document-upload")
                    ?.click()
                }
                className="customer-quote-btn customer-quote-btn-outline customer-quote-document-upload-btn"
              >
                {isUploadingDocument ? "Optimizing..." : "+ Upload Document"}
              </button>
              {documents.length > 0 && (
                <div className="customer-quote-document-list">
                  {documents.map((doc, index) => (
                    <div
                      className="customer-quote-document-item"
                      key={`${doc.name}-${index}`}
                    >
                      <button
                        type="button"
                        className="customer-quote-document-link"
                        onClick={() => openDocument(doc)}
                        title={
                          ["pdf", "png", "jpg", "jpeg"].includes(
                            documentExtension(doc.name),
                          )
                            ? `Preview ${doc.name}`
                            : `Download ${doc.name} to open in Word / Excel`
                        }
                      >
                        {doc.name}
                        {Number.isFinite(doc.size) &&
                          ` (${formatDocumentSize(doc.size)})`}
                        {doc.optimized ? " · compressed" : ""}
                      </button>
                      <button
                        type="button"
                        className="customer-quote-document-download"
                        onClick={() => downloadDocument(doc)}
                        aria-label={`Download ${doc.name}`}
                        title={`Download ${doc.name}`}
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        className="customer-quote-document-remove"
                        onClick={() => removeDocument(index)}
                        aria-label={`Remove ${doc.name}`}
                        title="Remove document"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <small className="customer-quote-document-limit">
                Up to 3 files · 5 MB each · stored on backend disk
              </small>
            </div>
          </div>
        </div>

        <div className="customer-quote-formula-note">
          <strong>Formula:</strong> Article Value + PM Fee − Discount
          {applyGst && gstMode === "exclusive" ? " + Exclusive GST" : ""}
          {
            " = Final Value. PM Fee and Discount are both calculated on Article Value only (PM Fee excluded from Discount)."
          }
          {applyGst && gstMode === "inclusive"
            ? " Inclusive GST is already included and is not added again."
            : ""}
        </div>
      </section>

      <div className="customer-quote-main-actions">
        <button
          type="button"
          onClick={handleSave}
          disabled={isUploadingDocument}
          className="customer-quote-btn customer-quote-btn-primary"
        >
          {isEdit ? "Update Quote" : "Save Custom Quote"}
        </button>

        {isEdit && (
          <button
            type="button"
            onClick={handleSaveAsNew}
            disabled={isUploadingDocument}
            className="customer-quote-btn customer-quote-btn-outline"
          >
            Save as New
          </button>
        )}

        <button
          type="button"
          onClick={exportToPDF}
          className="customer-quote-btn customer-quote-btn-pdf"
        >
          Export PDF
        </button>

        <button
          type="button"
          onClick={exportQuoteTableToExcel}
          className="customer-quote-btn customer-quote-btn-excel"
        >
          Export Excel
        </button>
      </div>

      {previewDocument && (
        <div className="customer-quote-preview-overlay" role="presentation">
          <div
            className="customer-quote-preview-dialog"
            role="dialog"
            aria-modal="true"
            aria-label={`Document preview: ${previewDocument.name}`}
          >
            <div className="customer-quote-preview-header">
              <strong title={previewDocument.name}>
                {previewDocument.name}
              </strong>
              <div className="customer-quote-preview-actions">
                <button
                  type="button"
                  onClick={() => downloadDocument(previewDocument.doc)}
                  className="customer-quote-btn customer-quote-btn-outline"
                >
                  Download
                </button>
                <button
                  type="button"
                  className="customer-quote-preview-close"
                  onClick={closeDocumentPreview}
                  aria-label="Close document preview"
                  title="Close"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="customer-quote-preview-body">
              {previewDocument.type === "pdf" ? (
                <iframe
                  title={`PDF: ${previewDocument.name}`}
                  src={previewDocument.url}
                  className="customer-quote-preview-frame"
                />
              ) : (
                <img
                  src={previewDocument.url}
                  alt={previewDocument.name}
                  className="customer-quote-preview-image"
                />
              )}
            </div>
            <div className="customer-quote-preview-footer">
              If your browser blocks PDF preview, click Download to open the
              file.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LabeledInput({ label, value, onChange, placeholder = " " }) {
  return (
    <div className="customer-quote-field">
      <label className="customer-quote-label">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="customer-quote-input"
        placeholder={placeholder}
      />
    </div>
  );
}
